<template>
  <div class="promo-code">
    <transition name="promo-code-fade" mode="out-in">
      <form key="code.edit" v-if="isEditing" @submit.prevent="submitCode" class="promo-code__edit">
        <Field
          :field="field"
          :model.sync="code"
          labelStyle="placeholder"
          fieldClass="promo-code__field"
        />
        <SubmitButton
          ref="submitCta"
          :buttonText="$t('checkout.promo_code.submit')"
          :onclick="submitCode"
          :isDisabled="isDisabled"
          :isSubmitting="isSubmitting"
          :errorCount="errorCount"
          :hasSubmitError="hasSubmitError"
          fieldClass="promo-code__cta"
          :ctaModifiers="['text-only']"
        />
      </form>
      <div key="code.view" v-else-if="hasPromoCode" class="promo-code__view">
        <div>
          <span class="promo-code__view-code">{{ promotionAdjustment.code }}</span>
          <a
            class="promo-code__remove-link"
            v-data-test-id="'promo-code-remove'"
            @click="removeCode"
          >
            <small>{{ $t('checkout.promo_code.remove') }}</small>
          </a>
        </div>
        <span>{{ discount }}</span>
      </div>
      <div
        key="code.add"
        v-else
        class="promo-code__add"
        :class="disabled ? 'promo-code__add--disabled' : ''"
        v-data-test-id="'promo-code-add'"
        @click="showEditCode"
      >
        {{ $t('checkout.promo_code.add') }}
        <span class="promo-code__plus icon-plus"></span>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import to from 'await-to-js'
import { isEmpty } from '@/helpers/object'
import { format } from '@/helpers/currency-formatter'
import Field from '@/components/_shared/Field.vue'
import SubmitButton from '@/components/_shared/SubmitButton.vue'
import { MESSAGE_TYPES } from '@/stores/flash'

export default {
  name: 'PromoCode',
  components: {
    Field,
    SubmitButton,
  },

  props: {
    flashContext: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      code: '',
      field: {
        key: 'promoCode',
        type: 'text',
        label: this.$t('checkout.promo_code.placeholder'),
        errors: [],
        on: {},
      },
      isEditing: false,
      isSubmitting: false,
      errorCount: 0,
      hasSubmitError: false,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapGetters({
      promotionAdjustment: 'order/promotionAdjustment',
      itemsLength: 'order/itemsLength',
    }),

    discount() {
      const amount = parseFloat(this.promotionAdjustment.amount)
      return format(amount, this.currency)
    },

    hasPromoCode() {
      return !isEmpty(this.promotionAdjustment)
    },

    isDisabled() {
      return !this.code
    },
  },

  watch: {
    itemsLength: 'updateEditingState',
  },

  methods: {
    ...mapActions({
      addFlashMessage: 'flash/addMessage',
      clearPromoCode: 'order/clearPromoCode',
    }),

    showEditCode() {
      if (!this.disabled) {
        this.isEditing = true
      }
    },

    showAddCode() {
      this.isEditing = false
      this.code = ''
    },

    updateEditingState() {
      if (this.itemsLength <= 0) {
        this.isEditing = false
      }
    },

    async removeCode() {
      if (!this.hasPromoCode) return
      this.isSubmitting = true
      const [error] = await to(this.clearPromoCode(this.promotionAdjustment.code))
      if (!isEmpty(error)) {
        this.showFlashMessage(error.type, error.text)
      } else {
        this.code = ''
      }
      this.isSubmitting = false
    },

    showFlashMessage(type, text) {
      this.$store.dispatch('flash/addMessage', {
        text: text || this.$t('errors.something_went_wrong'),
        type: MESSAGE_TYPES[type],
        context: this.flashContext,
      })
      if (!text) this.$rollbar.error('Error while submitting/removing the promo code.')
    },

    async submitCode() {
      this.isSubmitting = true
      const [error, result] = await to(this.$store.dispatch('order/applyPromoCode', this.code))
      const { type, text } = error || result
      if (result) this.isEditing = false
      this.isSubmitting = false
      this.showFlashMessage(type, text)
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-code {
  margin: 0 0 space(3);

  &__add,
  &__view,
  &__edit {
    min-height: space(12);
  }

  &__add,
  &__view {
    border-bottom: 1px solid #ddd;
    display: flex;
    align-items: center;
  }

  &__edit {
    margin-top: space(4);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;

    .cart__promo-code & {
      @media ($mq-large) {
        margin-top: 0;
      }
    }
  }

  &__add {
    color: $color-grey-dark-3;
    font-size: $font-size-small;
    line-height: space(4, $font-size-small);
    padding: space(3) 0;
    cursor: pointer;

    &--disabled {
      cursor: default;
      color: $color-grey-mid-2;
    }
  }

  &__plus {
    margin-left: space(2);
    font-size: $font-size-xsmall;
  }

  &__view {
    justify-content: space-between;
    padding: space(3) 0;
  }

  &__view-code {
    color: $color-grey-dark-3;
    font-size: $font-size-small;
  }

  &__remove-link {
    text-decoration: underline;
    color: $color-grey-mid-2;
    cursor: pointer;
    margin-left: space(5);
  }

  /deep/ .promo-code__cta {
    margin: 0;
  }

  // nest withing .promo-code to raise css prio
  .promo-code__field {
    flex: 1;
    margin: 0 space(4) 0 0;
    font-size: $font-size-small;
    line-height: space(4, $font-size-small);

    /deep/ .field__input--text {
      padding: $site-gutter $field-padding-horizontal;
      font-size: $font-size-normal;
      height: space(12);

      &::placeholder {
        font-style: normal;
        font-size: $font-size-small;
        padding: 2px 0;
      }
    }
  }

  .promo-code-fade-enter-active,
  .promo-code-fade-leave-active {
    transition: opacity 0.18s ease-in-out;
  }

  .promo-code-fade-enter,
  .promo-code-fade-leave-to {
    opacity: 0;
  }
}
</style>
